import { useApolloClient, useMutation } from "@apollo/client/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input } from "~/components/ui";
import { config } from "~/config";
import { GetUserForUpdateDocument, GetUsersDocument } from "~/features/admin/users/gqls";
import {
  CancelUserEmailChangeDocument,
  RefreshUserEmailChangeDocument,
  RequestUserEmailChangeDocument,
} from "~/features/admin/users/gqls/mutation/userEmailChange.generated";

export function UserEmailChange({ userId, currentEmail }: { userId: string; currentEmail: string }) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const [newEmail, setNewEmail] = useState("");
  const [pending, setPending] = useState<{ oldEmail: string; newEmail: string; status: string } | null>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [request] = useMutation(RequestUserEmailChangeDocument);
  const [refresh] = useMutation(RefreshUserEmailChangeDocument);
  const [cancel] = useMutation(CancelUserEmailChangeDocument);
  const confirmationUrl = new URL("/email-change", config.cotenAccountOrigin).href;

  const update = useCallback(async () => {
    setBusy(true);
    setMessage("");
    try {
      const result = await refresh({ variables: { userId } });
      const state = result.data?.cnutRefreshUserEmailChange ?? null;
      if (state === null || state.status === "applied") {
        setPending(null);
        setNewEmail("");
        await client.refetchQueries({ include: [GetUserForUpdateDocument, GetUsersDocument] });
        if (state?.status === "applied") {
          setMessage(t("feature.users.emailChange.applied", { defaultValue: "メールアドレスを変更しました。" }));
        }
      } else if (state?.status === "expired") {
        setPending(null);
        setMessage(
          t("feature.users.emailChange.expired", {
            defaultValue: "確認コードの期限が切れました。変更を開始し直してください。",
          }),
        );
      } else {
        setPending(state);
      }
    } catch {
      setMessage(
        t("feature.users.emailChange.failed", {
          defaultValue: "処理を完了できませんでした。状態を確認して再試行してください。",
        }),
      );
    } finally {
      setBusy(false);
    }
  }, [refresh, userId, client, t]);

  useEffect(() => {
    void update();
  }, [update]);

  const start = async () => {
    setBusy(true);
    setMessage("");
    try {
      const result = await request({ variables: { userId, newEmail: newEmail.trim().toLowerCase() } });
      setPending(result.data?.cnutRequestUserEmailChange ?? null);
    } catch {
      try {
        const result = await refresh({ variables: { userId } });
        setPending(result.data?.cnutRefreshUserEmailChange ?? null);
      } catch {
        setPending({ oldEmail: currentEmail, newEmail: newEmail.trim().toLowerCase(), status: "preparing" });
      }
      setMessage(
        t("feature.users.emailChange.failed", {
          defaultValue: "処理を完了できませんでした。状態を確認して再試行してください。",
        }),
      );
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    try {
      await cancel({ variables: { userId } });
      setPending(null);
      setMessage("");
    } catch {
      setMessage(
        t("feature.users.emailChange.cancelFailed", {
          defaultValue: "確認済みの変更は取消できません。状態確認で反映を完了してください。",
        }),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="space-y-2 rounded border p-3"
      aria-label={t("feature.users.emailChange.title", { defaultValue: "ログインメールの変更" })}
    >
      <p>
        {t("feature.users.emailChange.description", {
          defaultValue:
            "全アプリ共通のCOTEN IDログインメールを変更します。新メールの確認後に確定します。Google等の旧メールでのログイン連携は解除される場合があります。",
        })}
      </p>
      {pending ? (
        <>
          <p>
            {pending.oldEmail} → {pending.newEmail}
          </p>
          <p>
            {pending.status === "preparing"
              ? t("feature.users.emailChange.failed", {
                  defaultValue: "処理を完了できませんでした。状態を確認して再試行してください。",
                })
              : t("feature.users.emailChange.pending", {
                  defaultValue:
                    "受信者に確認画面と新メールに届いたコードで確認してもらってください。確認後、この画面で状態を確認すると反映が完了します。",
                })}
          </p>
          <a href={confirmationUrl} target="_blank" rel="noreferrer">
            {confirmationUrl}
          </a>
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => {
              void stop();
            }}
          >
            {t("feature.users.emailChange.cancel", { defaultValue: "確認待ちを取消" })}
          </Button>
        </>
      ) : (
        <>
          <Input
            type="email"
            aria-label={t("feature.users.emailChange.newEmail", { defaultValue: "新しいメールアドレス" })}
            value={newEmail}
            disabled={busy}
            onChange={(event) => setNewEmail(event.target.value)}
          />
          <Button
            type="button"
            disabled={busy || !newEmail.trim()}
            onClick={() => {
              void start();
            }}
          >
            {t("feature.users.emailChange.start", { defaultValue: "確認メールを送信" })}
          </Button>
        </>
      )}
      <Button
        type="button"
        variant="outline"
        disabled={busy}
        onClick={() => {
          void update();
        }}
      >
        {t("feature.users.emailChange.refresh", { defaultValue: "状態を確認・反映" })}
      </Button>
      {message && <output className="block">{message}</output>}
    </section>
  );
}
